"use client";
import { useState } from "react";
import { SortIcon } from "@soma/ui";
import { TaskRuns } from "@/app/dashboard/miner/[comp_id]/[hotkey]/_sections/TaskRuns";
import { TokenBars } from "@/app/dashboard/miner/[comp_id]/[hotkey]/_sections/TokenBars";
import type { MinerTask } from "@/lib/api";
import { dashboardContent, fmt } from "@/lib/content";
import { fmtScore } from "@/lib/dashboard/format";
import { BAND_TONE } from "@/components/ui/scoreBand";
import { categoryLabel } from "@/lib/dashboard/categories";
import {
  benchmarkFilters,
  fmtCompact,
  fmtRuns,
  sortTasks,
  taskGroups,
  taskKey,
  type TaskSortDirection,
  type TaskSortKey,
} from "@/lib/dashboard/miner";
import { signBand } from "@/lib/dashboard/scale";
import { cn } from "@/lib/utils";

/**
 * Competition tasks and the screener stages. Rows expand into the task's own token
 * comparison and every run behind it.
 *
 * Rows are keyed by benchmark type and task id together: the same task id appears under
 * several benchmarks, so the id alone would collapse two rows into one.
 */
export function MinerTasks({ tasks }: { tasks: MinerTask[] }) {
  const t = dashboardContent.miner.tasks;
  const groups = taskGroups(tasks);
  const [group, setGroup] = useState(groups[0]?.id ?? "competition");
  const [benchmark, setBenchmark] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<TaskSortKey>("default");
  const [sortDirection, setSortDirection] = useState<TaskSortDirection>("desc");

  if (groups.length === 0) {
    return <p className="rounded-md border border-line px-6 py-5 text-[14px] text-fg-muted">{t.empty}</p>;
  }

  const current = groups.find((g) => g.id === group) ?? groups[0];
  const filters = benchmarkFilters(current.tasks);
  const active = filters.some((f) => f.key === benchmark) ? benchmark : null;
  const filtered = active === null ? current.tasks : current.tasks.filter((task) => task.benchmarkType === active);
  const rows = sortTasks(filtered, sortKey, sortDirection);
  // Same column again flips the direction; a new one starts from the best.
  const sortBy = (key: TaskSortKey) => {
    if (key === sortKey) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDirection("desc");
    }
  };

  return (
    <section className="grid gap-4">
      {/* Two axes, two rows. Which round of work is one question - competition or one of
          the screener stages - and which benchmark inside it is another; every round runs
          all three. Side by side in one row they read as alternatives to each other. */}
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center gap-1">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-pressed={g.id === current.id}
              onClick={() => {
                setGroup(g.id);
                setOpen(null);
              }}
              className={cn(
                "cursor-pointer rounded-full px-3 py-1.5 text-[14px] transition-colors",
                g.id === current.id ? "bg-btn text-btn-fg" : "text-fg-muted hover:text-fg",
              )}
            >
              {t.groups[g.id]}
              <Count value={g.tasks.length} on={g.id === current.id} />
            </button>
          ))}
        </div>

        {filters.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {[{ key: null, count: current.tasks.length }, ...filters].map((f) => (
              <button
                key={f.key ?? "all"}
                type="button"
                aria-pressed={f.key === active}
                onClick={() => {
                  setBenchmark(f.key);
                  setOpen(null);
                }}
                className={cn(
                  "cursor-pointer rounded-full border px-3 py-1 text-[13px] transition-colors",
                  f.key === active
                    ? "border-fg-muted text-fg"
                    : "border-line text-fg-muted hover:border-fg-muted hover:text-fg",
                )}
              >
                {f.key === null ? t.allBenchmarks : categoryLabel(f.key)}
                <Count value={f.count} on={false} />
              </button>
            ))}
          </div>
        )}
      </div>

      <h2 className="eyebrow text-fg-muted">{t.headings[current.id]}</h2>

      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full border-collapse text-[14px]">
          <thead>
            <tr className="border-b border-line">
              <th className="w-10" />
              <th scope="col" className="eyebrow py-3.5 pr-4 text-left font-normal text-fg-faint">
                {t.columns.task}
              </th>
              <th scope="col" className="eyebrow py-3.5 text-right font-normal text-fg-faint">
                {t.columns.result}
              </th>
              <SortTh label={t.columns.score} active={sortKey === "score"} direction={sortDirection} onClick={() => sortBy("score")} />
              <th scope="col" className="eyebrow py-3.5 text-right font-normal text-fg-faint">
                {t.columns.runs}
              </th>
              <SortTh
                label={t.columns.tokens}
                active={sortKey === "tokens"}
                direction={sortDirection}
                onClick={() => sortBy("tokens")}
                className="pr-5"
              />
            </tr>
          </thead>
          <tbody>
            {rows.map((task) => {
              const key = taskKey(task);
              const expanded = open === key;
              return (
                <Row
                  key={key}
                  task={task}
                  expanded={expanded}
                  onToggle={() => setOpen(expanded ? null : key)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** The count beside a tab label, boxed so the tab's own digits do not run into it. */
function Count({ value, on }: { value: number; on: boolean }) {
  return (
    <span
      className={cn(
        "ml-2 rounded-full px-1.5 py-0.5 font-sans text-[11px] tabular-nums",
        on ? "bg-btn-fg/15" : "bg-fg/8 text-fg-muted",
      )}
    >
      {value}
    </span>
  );
}

/** A sortable header: the whole cell is the button, the arrow holds its width in every state. */
function SortTh({
  label,
  active,
  direction,
  onClick,
  className,
}: {
  label: string;
  active: boolean;
  direction: TaskSortDirection;
  onClick: () => void;
  className?: string;
}) {
  const aria = fmt(dashboardContent.miner.tasks.sortAria, { column: label });
  return (
    <th
      scope="col"
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
      className={cn("py-3.5 text-right font-normal", className)}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={aria}
        className={cn(
          "eyebrow inline-flex cursor-pointer items-center gap-1 transition-colors hover:text-fg",
          active ? "text-fg" : "text-fg-faint",
        )}
      >
        {label}
        <SortIcon direction={active ? direction : null} />
      </button>
    </th>
  );
}

function Row({
  task,
  expanded,
  onToggle,
}: {
  task: MinerTask;
  expanded: boolean;
  onToggle: () => void;
}) {
  const t = dashboardContent.miner.tasks;
  const counted = task.runCounts.baselineTotal !== null || task.runCounts.compressionTotal !== null;

  return (
    <>
      {/* The whole row is the toggle, same as a leaderboard row is the whole link. The
          chevron stays a real button so the keyboard has something to land on, and a
          click that ends a text selection is someone reading a number, not expanding. */}
      <tr
        onClick={(event) => {
          if ((event.target as HTMLElement).closest("a, button")) return;
          if (window.getSelection()?.toString()) return;
          onToggle();
        }}
        className={cn(
          "cursor-pointer border-b border-line-soft transition-colors hover:bg-bg-sunk",
          expanded && "bg-bg-sunk",
        )}
      >
        <td className="py-3.5 pl-4">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={fmt(t.expand, { task: task.task_name })}
            className="grid size-6 cursor-pointer place-items-center rounded-full text-fg-faint transition-colors hover:text-fg"
          >
            <span aria-hidden className={cn("transition-transform duration-200", expanded && "rotate-90")}>
              ›
            </span>
          </button>
        </td>
        <td className="py-3.5 pr-4">
          <span className="flex items-center gap-2">
            <span className="font-mono text-[13px]">{task.task_name}</span>
            {task.complexity && (
              <span className="rounded-full border border-line px-2 py-0.5 font-mono text-[11px] text-fg-muted">
                {task.complexity}
              </span>
            )}
          </span>
        </td>
        <td className="py-3.5 text-right font-sans tabular-nums text-[13px]">
          {counted ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-fg-muted">{fmtRuns(task.runCounts.baselinePassed, task.runCounts.baselineTotal)}</span>
              <span aria-hidden className="text-fg-faint">→</span>
              {/* Compression is judged on what it cost, not on matching exactly: a task
                  that passes MORE runs with compression than without has lost nothing.
                  Requiring equality painted 17/36 -> 31/36 red for improving. */}
              <span
                className={
                  (task.runCounts.compressionPassed ?? 0) >= (task.runCounts.baselinePassed ?? 0)
                    ? "text-score-top"
                    : "text-score-low"
                }
              >
                {fmtRuns(task.runCounts.compressionPassed, task.runCounts.compressionTotal)}
              </span>
            </span>
          ) : (
            <PassPair without={task.pass_without_compression} with_={task.pass_with_compression} />
          )}
        </td>
        <td className={cn("py-3.5 text-right font-sans tabular-nums", BAND_TONE[signBand(task.platform_score)])}>
          {fmtScore(task.platform_score)}
        </td>
        <td className="py-3.5 text-right font-sans tabular-nums text-fg-muted">{task.run_count}</td>
        <td className="py-3.5 pr-5 text-right font-sans tabular-nums text-fg-muted">
          {fmtCompact(task.tokens_with_compression)}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-line">
          <td colSpan={6} className="bg-bg-soft">
            <div className="grid gap-4 py-4">
              <div className="px-5">
                <p className="eyebrow mb-2 text-fg-faint">{dashboardContent.miner.tokens.taskTitle}</p>
                <div className="rounded-md border border-line bg-bg px-5 py-4">
                  <TokenBars tokens={task.tokens} scale="task" />
                </div>
              </div>
              <div className="overflow-x-auto px-5">
                <TaskRuns runs={task.runs} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Benchmarks without run counters still say whether the task passed on each side. */
function PassPair({ without, with_ }: { without: boolean | null; with_: boolean | null }) {
  const t = dashboardContent.miner.tasks;
  const mark = (v: boolean | null) =>
    v === null ? <span className="text-fg-faint">-</span> : (
      <span className={v ? "text-ok" : "text-accent"}>{v ? t.pass : t.fail}</span>
    );
  return (
    <span className="inline-flex items-center gap-1.5">
      {mark(without)}
      <span aria-hidden className="text-fg-faint">→</span>
      {mark(with_)}
    </span>
  );
}
